import { Reveal, MaskLine } from "@/components/site/Reveal";
import { Star, Quote, MapPin, PenLine } from "lucide-react";
import { TESTIMONIALS, RATING, SITE } from "@/data/content";

const Stars = ({ filled = 5 }) => (
  <div className="flex gap-1" aria-label={`${RATING.value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-5 w-5 ${i <= filled ? "fill-brand-gold text-brand-gold" : "text-brand-line"}`} />
    ))}
  </div>
);

const TestimonialsPage = () => (
  <main data-testid="page-testimonials">
    {/* HERO */}
    <section className="bg-brand-choc pt-36 pb-24 md:pt-48 md:pb-28 text-brand-cream">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <MaskLine><p className="eyebrow-gold">Testimonials · Goodwill, in writing</p></MaskLine>
        <h1 className="mt-6 font-display font-light tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-8xl">
          <MaskLine delay={0.1}>Trusted shelves,</MaskLine>
          <MaskLine delay={0.3}><span className="italic text-brand-gold">honest words.</span></MaskLine>
        </h1>
        <MaskLine delay={0.5}>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-brand-cream/80">
            Sixty years of goodwill, echoed by the people who stock us, ship us and buy us.
          </p>
        </MaskLine>
      </div>
    </section>

    {/* RATING BADGE */}
    <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-16 md:py-24">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 rounded-2xl border border-brand-line bg-white p-8 md:p-12" data-testid="rating-badge">
          <div className="flex items-center gap-8">
            <p className="font-display text-7xl md:text-8xl font-light text-brand-red leading-none">{RATING.value}</p>
            <div>
              <Stars filled={4} />
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-brand-muted">
                {RATING.source}
              </p>
              <p className="mt-1 text-sm text-brand-muted">{RATING.note}</p>
            </div>
          </div>
          <a
            href={SITE.mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-review-link"
            className="inline-flex items-center gap-3 rounded-full bg-brand-choc px-7 py-4 text-sm font-bold text-brand-cream transition-colors duration-300 hover:bg-brand-red"
          >
            <PenLine className="h-4 w-4" /> Found us on Google? Leave a review
          </a>
        </div>
      </Reveal>
    </section>

    {/* QUOTES */}
    <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pb-24 md:pb-32">
      <div className="grid gap-6 md:grid-cols-2" data-testid="testimonial-grid">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name + i} delay={(i % 2) * 0.08} className="h-full">
            <figure className="flex h-full flex-col rounded-2xl border border-brand-line bg-brand-sand/50 p-8 md:p-10">
              <Quote className="h-8 w-8 text-brand-gold" />
              <blockquote className="mt-6 flex-1 font-display text-xl md:text-2xl font-light leading-relaxed">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-8 flex items-center justify-between gap-4 border-t border-brand-line pt-5">
                <div>
                  <p className="text-sm font-bold">{t.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted">{t.place}</p>
                </div>
                <Stars filled={5} />
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-10 flex items-center gap-2 text-sm text-brand-muted">
          <MapPin className="h-4 w-4 text-brand-gold" />
          Trade feedback from partners across India, Africa and the Gulf. Names shared on request.
        </p>
      </Reveal>
    </section>
  </main>
);

export default TestimonialsPage;
